import type { LocationQuery, RouteLocationRaw, RouteMeta } from 'vue-router'

/**
 * MYAC-1522 — the Pilotage pages have addresses of their own
 * (`/qms/{pid}/conversation`, `/qms/{pid}/essentials`, …) instead of one page
 * with `?tab=`. Since 3b step 5a they are NESTED routes: one parent record per
 * track, whose component is the Pilotage layout (`ProductReferentialsView.vue`,
 * it stays mounted from one section to the next), and one child record per
 * section, whose component is that section's page. The tab names stay the
 * layout's own vocabulary; the sections are what the address says.
 *
 * Route names: `{qms|products}-pilotage-{section}` (`qms-pilotage-compliance`),
 * each child carrying `meta.section`. Every piece of code that builds or reads a
 * Pilotage location goes through this module, never through a literal name.
 *
 * `?tab=` is still accepted as an INPUT: `/referentials?tab=x` and a `?tab=` on
 * a section's address are translated to the section's address, every other
 * parameter kept (`translateLegacyPilotage`). Old links, bookmarks and the
 * e-mails' bare `/referentials` all land on a clean address.
 */
export const PILOTAGE_SECTION_OF_TAB = {
  chat: 'conversation',
  manage: 'standards',
  summary: 'essentials',
  assessment: 'compliance',
  'action-plan': 'action-plan',
  consistency: 'consistency',
  continuity: 'continuity',
  completeness: 'completeness',
} as const

export type PilotageTab = keyof typeof PILOTAGE_SECTION_OF_TAB
export type PilotageSection = (typeof PILOTAGE_SECTION_OF_TAB)[PilotageTab]
/** The two route trees the Pilotage lives under: `/qms/:pid` and `/products/:pid`. */
export type PilotageTrack = 'qms' | 'products'

/** The page's default when the address names nothing: the conversation (MYAC-1313). */
export const PILOTAGE_DEFAULT_TAB: PilotageTab = 'chat'

/** Every section, in the table's order: one child route each. */
export const PILOTAGE_SECTIONS = Object.values(PILOTAGE_SECTION_OF_TAB) as PilotageSection[]

const TAB_OF_SECTION = Object.fromEntries(
  Object.entries(PILOTAGE_SECTION_OF_TAB).map(([tab, section]) => [section, tab]),
) as Record<PilotageSection, PilotageTab>

export function isPilotageTab(value: unknown): value is PilotageTab {
  return typeof value === 'string' && value in PILOTAGE_SECTION_OF_TAB
}

export function tabOfSection(section: unknown): PilotageTab | null {
  return typeof section === 'string' && section in TAB_OF_SECTION
    ? TAB_OF_SECTION[section as PilotageSection]
    : null
}

/**
 * The route tree of a track (`qms`, `td`), or of any route name under it
 * (`products-referentials`, `qms-pilotage-compliance`).
 */
export function pilotageTrack(trackOrRoute: string): PilotageTrack {
  return trackOrRoute === 'td' || trackOrRoute.startsWith('products') ? 'products' : 'qms'
}

/** The child route of one section: `qms-pilotage-compliance`. */
export function pilotageSectionRouteName(track: PilotageTrack, section: PilotageSection): string {
  return `${track}-pilotage-${section}`
}

/** The route of a tab, for a track or for any route name under that track. */
export function pilotageRouteName(trackOrRoute: string, tab: PilotageTab): string {
  return pilotageSectionRouteName(pilotageTrack(trackOrRoute), PILOTAGE_SECTION_OF_TAB[tab])
}

const SECTION_OF_ROUTE_NAME = new Map<string, PilotageSection>(
  (['qms', 'products'] as const).flatMap((track) =>
    PILOTAGE_SECTIONS.map((section) => [pilotageSectionRouteName(track, section), section] as const),
  ),
)

/** Every Pilotage section route name, both tracks (CSM `ROUTE_ACTIONS`). */
export const PILOTAGE_ROUTE_NAMES: readonly string[] = [...SECTION_OF_ROUTE_NAME.keys()]

/** Is this the name of one of the section routes (either track)? */
export function isPilotageRoute(name: unknown): boolean {
  return typeof name === 'string' && SECTION_OF_ROUTE_NAME.has(name)
}

/**
 * The tab a location shows: its section (the route's `meta.section`, else its
 * name), else a legacy `?tab=`, else null. Both the section routes and the
 * legacy `*-referentials` name are read, so a caller holding either kind of
 * location gets the same answer.
 */
export function tabOfLocation(route: { name?: unknown, meta?: RouteMeta, query?: LocationQuery }): PilotageTab | null {
  if (isPilotageRoute(route.name)) {
    const fromSection = tabOfSection(route.meta?.section) ?? tabOfSection(SECTION_OF_ROUTE_NAME.get(route.name as string))
    if (fromSection) return fromSection
  }
  const tab = route.query?.tab
  return isPilotageTab(tab) ? tab : null
}

/** The location of a Pilotage page: `{ name: '<track>-pilotage-<section>', params: { pid }, query }`. */
export function pilotageLocation(
  trackOrRoute: string,
  pid: string,
  tab: PilotageTab = PILOTAGE_DEFAULT_TAB,
  query: LocationQuery = {},
): RouteLocationRaw {
  return {
    name: pilotageRouteName(trackOrRoute, tab),
    params: { pid },
    query,
  }
}

/**
 * A requirement on Conformité, opened, in the standard it belongs to
 * (MYAC-1396): the one link every « where it comes from » on an action uses —
 * the origin section and the review's header chips (MYAC-1662).
 */
export function requirementLocation(
  trackOrRoute: string,
  pid: string,
  uid: string,
  adoptionId?: string | null,
): RouteLocationRaw {
  return pilotageLocation(
    trackOrRoute,
    pid,
    'assessment',
    adoptionId ? { req_uid: uid, req_adoption: adoptionId } : { req_uid: uid },
  )
}

/**
 * The translation layer: a legacy Pilotage location (`*-referentials`, with or
 * without `?tab=`) or a section address carrying `?tab=` → the clean section
 * address, every other query parameter kept, the hash too. An unknown `?tab=`
 * keeps the section the address names (or the default), never an error page.
 *
 * A legacy link naming no tab is decided on its other parameters, as the page
 * always did: `?task=` opens the action plan (MYAC-1079), `?gap_task=` the
 * completeness page.
 */
export function translateLegacyPilotage(to: { name?: unknown, meta?: RouteMeta, params: Record<string, unknown>, query: LocationQuery, hash?: string }): RouteLocationRaw {
  const { tab, ...rest } = to.query
  const target: PilotageTab = isPilotageTab(tab)
    ? tab
    : (isPilotageRoute(to.name) ? tabOfLocation({ name: to.name, meta: to.meta }) : null)
      ?? (nonEmpty(rest.task) ? 'action-plan' : nonEmpty(rest.gap_task) ? 'completeness' : PILOTAGE_DEFAULT_TAB)
  return {
    name: pilotageRouteName(String(to.name ?? ''), target),
    params: { pid: String(to.params.pid ?? '') },
    query: rest,
    hash: to.hash,
  }
}

/**
 * The router's global `?tab=` guard: a `?tab=` on a section's own address (an
 * old internal push, a pasted link) becomes that section's clean address, the
 * other parameters kept. Registered by `src/router/index.ts` and by the test
 * harness, so both run the same one.
 */
export function pilotageTabGuard(to: { name?: unknown, meta?: RouteMeta, params: Record<string, unknown>, query: LocationQuery, hash?: string }): true | RouteLocationRaw {
  if (isPilotageRoute(to.name) && typeof to.query.tab === 'string') return translateLegacyPilotage(to)
  return true
}

function nonEmpty(value: unknown): boolean {
  return typeof value === 'string' && value.length > 0
}

// ── Each section keeps its filters (MYAC-1522 3b, drift D11) ──────────────────

/**
 * The URL filters of a section: the `useUrlFilterState` prefix its panel
 * writes (`plan_nest`, `coherence_sev`, `continuity_status`…). A section with
 * no entry has no URL filter.
 */
export const PILOTAGE_FILTER_PREFIX: Partial<Record<PilotageSection, string>> = {
  'action-plan': 'plan',
  consistency: 'coherence',
  continuity: 'continuity',
}

/** The section a location shows, read off its route name (either track), or null. */
export function sectionOfRouteName(name: unknown): PilotageSection | null {
  return typeof name === 'string' ? (SECTION_OF_ROUTE_NAME.get(name) ?? null) : null
}

/** The section whose filter this query parameter is, or null for any other parameter. */
function filterSectionOf(param: string): PilotageSection | null {
  for (const [section, prefix] of Object.entries(PILOTAGE_FILTER_PREFIX)) {
    if (param.startsWith(`${prefix}_`)) return section as PilotageSection
  }
  return null
}

/** The query parameters of one section's own filters. */
export function sectionFilters(query: LocationQuery, section: PilotageSection): LocationQuery {
  return Object.fromEntries(Object.entries(query).filter(([key]) => filterSectionOf(key) === section))
}

type GuardLocation = { name?: unknown, params: Record<string, unknown>, query: LocationQuery, hash?: string }

/**
 * Entering a section, its URL shows ITS filters: the ones the link carries
 * when it carries any (an explicit link wins), else the ones the member left
 * it with in this project (`remembered`), and never another section's.
 *
 * Every way in goes through here — the project menu and the phone row (links
 * with no query), the layout's hand-overs (which carry the query they leave),
 * the Résumé's and the task sheet's `?tab=` pushes — so they all land alike.
 * A redirect of the navigation itself: the page mounts already filtered, the
 * address bar shows the filters, and Back walks to the section left. A query
 * change inside a section (its own filters moving) is not an entry. The
 * hand-over parameters (`?task`, `?gap_task`, `?req`, `?req_uid`) are no
 * section's filter: carried as the link carries them, never remembered.
 */
export function restoreSectionFilters(
  to: GuardLocation,
  from: { name?: unknown, params: Record<string, unknown> },
  remembered: (pid: string, section: PilotageSection) => LocationQuery | undefined,
): true | RouteLocationRaw {
  const section = sectionOfRouteName(to.name)
  if (!section) return true
  const pid = String(to.params.pid ?? '')
  if (sectionOfRouteName(from.name) === section && String(from.params.pid ?? '') === pid) return true
  const own = sectionFilters(to.query, section)
  const kept = Object.keys(own).length > 0 ? {} : (remembered(pid, section) ?? {})
  const foreign = Object.keys(to.query).filter((key) => {
    const of = filterSectionOf(key)
    return of !== null && of !== section
  })
  if (foreign.length === 0 && Object.keys(kept).length === 0) return true
  const query: LocationQuery = { ...to.query, ...kept }
  for (const key of foreign) delete query[key]
  return { name: String(to.name), params: { pid }, query, hash: to.hash }
}
